import { Component, OnInit, OnDestroy, Input } from '@angular/core';

import { AlertService, MessageSeverity, DialogType } from '../../services/alert.service';
import { AuthService } from '../../services/auth.service';
import { ConfigurationService } from '../../services/configuration.service';
import { Utilities } from '../../services/utilities';
import { UserLogin } from '../../models/user-login.model';
import { SFLogin } from '../../models/user.model';
import { SfDataService } from '../../services/sf-data.service';
import { ResponseAPI } from '../../models/IDPReport.model';
import { Router } from '@angular/router';
@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false
})

export class LoginComponent implements OnInit, OnDestroy {

  userLogin = new UserLogin();
  sFLogin = new SFLogin();
  isLoading = false;
  formResetToggle = true;
  modalClosedCallback: () => void;
  loginStatusSubscription: any;
  requiresMfa = false;
  mfaCode = '';
  tempToken: string | null = null;
  rememberDevice = false;
  @Input()
  isModal = false;


  constructor(private alertService: AlertService, private authService: AuthService, private configurations: ConfigurationService, private sfDataService: SfDataService, private router: Router)
  {

  }


  ngOnInit() {

    this.userLogin.rememberMe = this.authService.rememberMe;

    if (this.getShouldRedirect()) {
      this.authService.redirectLoginUser();
    } else {
      this.loginStatusSubscription = this.authService.getLoginStatusEvent().subscribe(isLoggedIn => {
        if (this.getShouldRedirect()) {
          this.authService.redirectLoginUser();
        }
      });
    }
  }


  ngOnDestroy() {
    if (this.loginStatusSubscription) {
      this.loginStatusSubscription.unsubscribe();
    }
  }


  getShouldRedirect() {
    return !this.isModal && this.authService.isLoggedIn && !this.authService.isSessionExpired;
  }


  showErrorAlert(caption: string, message: string) {
    this.alertService.showMessage(caption, message, MessageSeverity.error);
  }

  closeModal() {
    if (this.modalClosedCallback) {
      this.modalClosedCallback();
    }
  }


  login() {
    this.isLoading = true;
    let responseAPI = new ResponseAPI();
    this.alertService.startLoadingMessage('', 'Attempting login...');
    this.sFLogin.username = this.userLogin.userName;
    this.sFLogin.password = this.userLogin.password;
    

    this.authService.loginWithPassword(this.userLogin.userName, this.userLogin.password, this.userLogin.rememberMe)
      .subscribe(
        (response) => {
          setTimeout(() => {
            this.alertService.stopLoadingMessage();
            this.isLoading = false;


            if ((response as any).mfa_required) {

              const mfaResponse = response as any;

              this.requiresMfa = true;
              this.tempToken = mfaResponse.mfa_token;  // ✅ no TS error now
              return;
            }
            const user = response;

            this.reset();

            if (!this.isModal) {
              this.alertService.showMessage('Login', `Welcome ${user.userName}!`, MessageSeverity.success);
            } else {
              this.alertService.showMessage('Login', `Session for ${user.userName} restored!`, MessageSeverity.success);
              setTimeout(() => {
                this.alertService.showStickyMessage('Session Restored', 'Please try your last operation again', MessageSeverity.default);
              }, 500);

              this.closeModal();
            }
          }, 500);
        },
        error => {

          this.alertService.stopLoadingMessage();
          this.isLoading = false;

          

          if (error?.error?.mfa_setup_required) {
            const userId = error.error.user_id;
            this.router.navigate(['/mfa-setup'], { queryParams: { userId } });
          }

          // 🔥 HANDLE MFA RESPONSE HERE
          if (error?.error?.mfa_required) {
            this.requiresMfa = true;
            this.tempToken = error.error.mfa_token;
            if (error?.error?.mfa_setup_required) {
              const userId = error.error.user_id;
              this.router.navigate(['/mfa-setup'], { queryParams: { userId } });
              return;
            }
            return;
          }

          if (error?.error?.password_change_required) {

            const userId = error.error.user_id;

            this.router.navigate(['/settings'], { fragment: 'profile' });
            this.alertService.showStickyMessage(
              'Change the Password',
              'Change the Password.',
              MessageSeverity.error, error);

            return;
          }

          // 🔴 Existing normal error handling
          if (Utilities.checkNoNetwork(error)) {
            this.alertService.showStickyMessage(
              Utilities.noNetworkMessageCaption,
              Utilities.noNetworkMessageDetail,
              MessageSeverity.error, error);
            this.offerAlternateHost();
          }
          else {
            const errorMessage = Utilities.getHttpResponseMessage(error);

            if (errorMessage) {
              this.alertService.showStickyMessage(
                'Unable to login',
                this.mapLoginErrorMessage(errorMessage),
                MessageSeverity.error, error);
            }
            else {
              this.alertService.showStickyMessage(
                'Unable to login',
                'An error occurred whilst logging in, please try again later.',
                MessageSeverity.error, error);
            }
          }
        }

    );
  } verifyMfa() {

    if (!this.mfaCode || this.mfaCode.length !== 6) {
      this.showErrorAlert('Invalid Code', 'Enter the 6-digit code');
      return;
    }

    this.isLoading = true;

    this.authService.verifyMfa(this.tempToken!, this.mfaCode, this.userLogin.rememberMe, this.rememberDevice)
      .subscribe({
        next: (user) => {

          this.isLoading = false;
          // Do NOT set requiresMfa = false here — the login form would flash
          // during async router navigation and the user could re-submit credentials.
          // The component is destroyed when navigation completes.
          this.mfaCode = '';
          this.tempToken = null;

          this.alertService.showMessage(
            'Login',
            `Welcome ${user.userName}!`,
            MessageSeverity.success
          );

          // Redirect to dashboard
          this.authService.redirectLoginUser();
        },

        error: (error) => {

          this.isLoading = false;

          // Show friendly message instead of Angular error
          this.alertService.showMessage(
            'Invalid Code',
            'The authentication code is incorrect. Please try again.',
            MessageSeverity.error
          );

          this.mfaCode = '';
        }
      });
  }


  offerAlternateHost() {

    if (Utilities.checkIsLocalHost(location.origin) && Utilities.checkIsLocalHost(this.configurations.baseUrl)) {
      this.alertService.showDialog('Dear Developer!\nIt appears your backend Web API service is not running...\n' +
        'Would you want to temporarily switch to the online Demo API below?(Or specify another)',
        DialogType.prompt,
        (value: string) => {
          this.configurations.baseUrl = value;
          this.alertService.showStickyMessage('API Changed!', 'The target Web API has been changed to: ' + value, MessageSeverity.warn);
        },
        null,
        null,
        null,
        this.configurations.fallbackBaseUrl);
    }
  }


  mapLoginErrorMessage(error: string) {

    if (error === 'invalid_username_or_password') {
      return 'Invalid username or password';
    }

    if (error === 'invalid_grant') {
      return 'This account has been disabled';
    }

    return error;
  }


  reset() {
    this.formResetToggle = false;

    setTimeout(() => {
      this.formResetToggle = true;
    });
  }
}
